import {
  setUsers,
  setLoadingUsers,
  setUserFormValues,
  setLoadingUser,
  setPasswordWasReset,
  setDefaultUser,
  setShowDeactivated,
} from '../../store/users/usersSlice';
import apiService from '@sonifi/wb-core/services/api.service';
import {
  setAddExpanded,
  setExpandedRow,
  setTablePagination,
} from '@sonifi/wb-widget/store/tableConfig/tableConfigSlice';
import { TABLE_KEYS } from '@sonifi/wb-widget/store/tableConfig/tableConfigConstants';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { parseQueryParams } from '@sonifi/wb-core/utils/parseHelper';
import { getUsersMapped, mapCreateUserErrors } from './usersHelper';
import { setFormErrors } from '@sonifi/wb-widget/store/formErrors/formErrorsSlice';
import { FORM_ERRORS_IDS } from '@sonifi/wb-widget/store/formErrors/formErrorsConstants';
import { GROUPS_KEYS } from '@sonifi/wb-group/store/groupsConstants';

export const getUsers =
  ({
    page = 1,
    pageSize = 100,
    query,
    sortBy = 'email',
    sortDir = 'asc',
    showDeactivated = false,
  } = {}) =>
  async (dispatch, getState) => {
    const params = {
      page,
      _query: query,
      rpp: pageSize,
      _order_by: `${sortDir === 'desc' ? '-' : ''}${sortBy}`,
      // "Hide deactivated users" filters on the server so a page holds a
      // full set of active users. `parseQueryParams` drops falsy values, so
      // `undefined` omits the param entirely.
      enabled: showDeactivated ? undefined : true,
    };
    try {
      dispatch(setLoadingUsers(LOADING_STATUS.loading));
      const url = `${ENDPOINTS.USERS}?${parseQueryParams(params)}`;
      const res = await apiService.get(url);
      const users = res.data?.objects || [];
      dispatch(
        setTablePagination({
          page,
          pageSize,
          query,
          sortBy,
          sortDir,
          total: res.data.total,
          tableKey: TABLE_KEYS.adminUsers,
        }),
      );
      dispatch(setShowDeactivated(showDeactivated));

      const {
        groups: { groups },
        user: { permissions },
      } = getState();
      const usersMapped = getUsersMapped({
        groups: groups[GROUPS_KEYS.allGroups],
        permissions,
        users,
      });
      dispatch(setUsers({ users: usersMapped }));
      dispatch(setLoadingUsers(LOADING_STATUS.success));
    } catch (error) {
      dispatch(setLoadingUsers(LOADING_STATUS.error));
    }
  };

export const getUserById = (id) => async (dispatch, getState) => {
  try {
    const {
      adminUsers: { users },
      groups: { groups },
      user: { permissions },
    } = getState();

    dispatch(setPasswordWasReset(false));

    // Check if the user is already in the Store
    let user = users?.find((us) => us.id === parseInt(id));
    if (!user) {
      const url = `${ENDPOINTS.USERS}/${id}`;
      const res = await apiService.get(url);
      const userRes = res.data;
      user = getUsersMapped({
        groups: groups[GROUPS_KEYS.allGroups],
        permissions,
        users: [userRes],
      })[0];
    }

    dispatch(setUserFormValues({ user }));
  } catch (error) {
    console.error(error);
  }
};

export const initRowExpanded = (id, add) => async (dispatch, getState) => {
  const {
    adminUsers: { users },
  } = getState();
  dispatch(
    setAddExpanded({
      isExpanded: !!add,
      tableKey: TABLE_KEYS.adminUsers,
    }),
  );

  // React Table keys expanded rows by position in the rendered data, which
  // is `users` as fetched (filtering happens on the server).
  const userIndex = users.findIndex((u) => u.id === parseInt(id));
  dispatch(
    setExpandedRow({
      expanded: userIndex !== -1 ? { [userIndex]: true } : {},
      tableKey: TABLE_KEYS.adminUsers,
    }),
  );
};

export const clearRowsAndForm = () => async (dispatch) => {
  dispatch(setDefaultUser());
  dispatch(
    setAddExpanded({ isExpanded: false, tableKey: TABLE_KEYS.adminUsers }),
  );
  dispatch(setExpandedRow({ expanded: {}, tableKey: TABLE_KEYS.adminUsers }));
};

export const submitUser = (user) => async (dispatch, getState) => {
  dispatch(setLoadingUser(LOADING_STATUS.loading));
  try {
    const userPayload = {
      all_groups: user.all_groups,
      email: user.email,
      enabled: user.enabled,
      first_name: user.first_name,
      last_name: user.last_name,
      groups: user.groups.map((g) => g.uri),
      permissions: user.permissions,
    };
    const url = user.id ? `${ENDPOINTS.USERS}/${user.id}` : ENDPOINTS.USERS;
    const res = user.id
      ? await apiService.patch(url, userPayload)
      : await apiService.post(url, userPayload);
    const userRes = res.data;
    // Update Store
    const {
      adminUsers: { users },
      groups: { groups },
      user: { permissions },
    } = getState();
    const userMapped = getUsersMapped({
      groups: groups[GROUPS_KEYS.allGroups],
      permissions,
      users: [userRes],
    })[0];
    const usersUpdated = [...users];
    if (user.id) {
      const userIndex = users.findIndex((u) => u.id === userMapped.id);
      usersUpdated[userIndex] = userMapped;
    } else {
      usersUpdated.unshift(userMapped);
    }
    dispatch(setUsers({ users: usersUpdated }));
    dispatch(setLoadingUser(LOADING_STATUS.success));
    return user;
  } catch (errors) {
    console.error(errors);
    dispatch(setLoadingUser(LOADING_STATUS.error));
    dispatch(
      setFormErrors({
        id: FORM_ERRORS_IDS.adminUserForm,
        errors: user.id ? errors : mapCreateUserErrors(errors),
      }),
    );
    return Promise.reject(errors);
  }
};

export const resetPassword =
  ({ email, link }) =>
  async (dispatch) => {
    try {
      dispatch(setPasswordWasReset(false));
      const url = 'users/password_reset_tokens';
      await apiService.post(url, { email, link });
      dispatch(setPasswordWasReset(true));
    } catch (error) {
      console.error(error);
    }
  };
